import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Col } from "reactstrap"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import "../../../assets/css/colors.css";
import { useLanguage } from "context/LanguageProvider";


const HeapFunctionsGame = () => {
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();
    const [correctAnswer, setCorrectAnswer] = useState(false);
    const [gameState, setGameState] = useState({
        currentLevel: 0,
        score: 0,
        streak: 0,
        maxStreak: 0,
        lives: 3
    });

    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [gameStatus, setGameStatus] = useState('playing'); // playing, won, lost
    const [lives, setLivesLeft] = useState(3);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState(null);

    const handlePlayAgain = () => {
        resetGame();
    };

    const buttonStyle = {
        padding: '16px 24px',
        borderRadius: '16px',
        border: 'none',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontFamily: 'inherit',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        background: "#1e293b",
        fontFamily: "Poppins, sans-serif",
        color: "#d9dde7ff",
        border: "1px solid #3251ff94"
    };

    const levels = [
        {
            id: 1,
            question: selLanguage
                ? "Έχουμε έναν κενό πίνακα και θέλουμε να δημιουργήσουμε ένα έγκυρο Heap από τις τιμές [4, 1, 3, 2, 16, 9, 10]. Ποια συνάρτηση χρειαζόμαστε;"
                : "We have an empty array and want to build a valid heap from the values [4, 1, 3, 2, 16, 9, 10]. Which function do we need?",
            heap1: [],
            heap2: [],
            showHeap2: false,
            correctAnswer: "heapify()",
            options: ["heapify()", "sift_up()", "peek()", "merge()"],
            explanation: selLanguage
                ? "Η heapify() μετατρέπει έναν πίνακα σε έγκυρο Heap σε O(n) χρόνο."
                : "heapify() turns an array into a valid heap in O(n) time.",
            trap: false
        },
        {
            id: 2,
            question: selLanguage
                ? "Προσθέσαμε το στοιχείο 1 στο τέλος του Heap [2, 4, 3, 8, 7, 9]. Ποια συνάρτηση θα επαναφέρει την ιδιότητα του Heap;"
                : "We added element 1 to the end of the heap [2, 4, 3, 8, 7, 9]. Which function will restore the heap property?",
            heap1: [2, 4, 3, 8, 7, 9, 1],
            heap2: [],
            showHeap2: false,
            correctAnswer: "sift_up()",
            options: ["sift_down()", "sift_up()", "heapify()", "peek()"],
            explanation: selLanguage
                ? "Το sift_up() ανεβάζει το νέο στοιχείο στη σωστή θέση συγκρίνοντάς το με τους γονείς του."
                : "sift_up() moves the new element up to the correct position by comparing it with its parents.",
            trap: false
        },
        {
            id: 3,
            question: selLanguage
                ? "Θέλουμε να δούμε ποιο είναι το μικρότερο στοιχείο στο Min-Heap [1, 3, 6, 5, 9, 8] χωρίς να το αφαιρέσουμε:"
                : "We want to see the smallest element in the min-heap [1, 3, 6, 5, 9, 8] without removing it:",
            heap1: [1, 3, 6, 5, 9, 8],
            heap2: [],
            showHeap2: false,
            correctAnswer: "peek()",
            options: ["sift_down()", "size()", "is_empty()", "peek()"],
            explanation: selLanguage
                ? "Η peek() επιστρέφει τη ρίζα (το μικρότερο στοιχείο σε Min-Heap) σε O(1) χρόνο χωρίς αλλαγές."
                : "peek() returns the root (the smallest element in a min-heap) in O(1) time without modifying it.",
            trap: false
        },
        {
            id: 4,
            question: selLanguage
                ? "Έχουμε δύο Heaps [1, 4, 6] και [2, 5, 7] και θέλουμε να τα ενώσουμε σε ένα νέο Heap:"
                : "We have two heaps [1, 4, 6] and [2, 5, 7] and want to merge them into a new heap:",
            heap1: [1, 4, 6],
            heap2: [2, 5, 7],
            showHeap2: true,
            correctAnswer: "merge()",
            options: ["merge()", "sift_up()", "Merge()", "merge_heap()"],
            explanation: selLanguage
                ? "Η merge() συνδυάζει δύο Heaps και κάνει heapify() για να διατηρήσει την ιδιότητα."
                : "merge() combines two heaps and then heapifies to maintain the heap property.",
            trap: false
        },
        {
            id: 5,
            question: selLanguage
                ? "Αφαιρέσαμε τη ρίζα από το Heap και βάλαμε το τελευταίο στοιχείο στην κορυφή. Τι κάνουμε τώρα;"
                : "We removed the root from the heap and placed the last element at the top. What do we do now?",
            heap1: [10, 3, 6, 5, 9, 8], // 10 είναι λάθος στην κορυφή
            heap2: [],
            showHeap2: false,
            correctAnswer: "sift_down()",
            options: ["sift_up()", "sift_down()", "peek()", "is_empty()"],
            explanation: selLanguage
                ? "Το sift_down() κατεβάζει το στοιχείο στη σωστή θέση. Το sift_up() θα ήταν λάθος γιατί το στοιχείο πρέπει να κατέβει!"
                : "sift_down() pushes the element down to its correct position. sift_up() would be wrong because the element needs to move down!",
            trap: true
        },
        {
            id: 6,
            question: selLanguage
                ? "Θέλουμε να ελέγξουμε αν μπορούμε να κάνουμε κάποια λειτουργία σε ένα Heap πριν προσπαθήσουμε:"
                : "We want to check whether we can perform an operation on a heap before we try:",
            heap1: [],
            heap2: [],
            showHeap2: false,
            correctAnswer: "is_empty()",
            options: ["is_empty()", "size()", "peek()", "heapify()"],
            explanation: selLanguage
                ? "Η is_empty() ελέγχει αν το Heap είναι άδειο - χρήσιμο για αποφυγή λαθών."
                : "is_empty() checks if the heap is empty – useful to avoid errors.",
            trap: false
        },
        {
            id: 7,
            question: selLanguage
                ? "Έχουμε ένα άδειο Heap και θέλουμε να προσθέσουμε το πρώτο στοιχείο. Ποια συνάρτηση χρειαζόμαστε;"
                : "We have an empty heap and want to add the first element. Which function do we need?",
            heap1: [],
            heap2: [],
            showHeap2: false,
            correctAnswer: selLanguage
                ? "απλή εισαγωγή"
                : "simple insertion",
            options: selLanguage
                ? ["heapify()", "sift_up()", "sift_down()", "απλή εισαγωγή"]
                : ["heapify()", "sift_up()", "sift_down()", "simple insertion"],
            explanation: selLanguage
                ? "Παγίδα! Το πρώτο στοιχείο μπαίνει απευθείας - δεν χρειάζεται καμία συνάρτηση αναδόμησης!"
                : "Trap! The first element is inserted directly – no restructuring function is needed!",
            trap: true
        }
    ];


    const currentLevel = levels[gameState.currentLevel];



    const heapFunctions = [
        { name: "heapify()" },
        { name: "sift_up()" },
        { name: "sift_down()" },
        { name: "peek()" },
        { name: "merge()" },
        { name: "size()" },
        { name: "is_empty()" },
        { name: "Καμία - απλή εισαγωγή" }
    ];

    function shuffleArray(array) {
        return array
            .map(item => ({ item, sort: Math.random() }))
            .sort((a, b) => a.sort - b.sort)
            .map(({ item }) => item);
    }

    const handleAnswerSelect = (answer) => {
        setSelectedAnswer(answer);
        setShowFeedback(true);

        const isCorrect = answer === currentLevel.correctAnswer;

        if (isCorrect) {
            setFeedbackMessage(selLanguage ? "Σωστή απάντηση!" : "Correct answer!");
            setCorrectAnswer(true);
            setGameState(prev => ({
                ...prev,
                score: prev.score + (currentLevel.trap ? 20 : 10),
                streak: prev.streak + 1,
                maxStreak: Math.max(prev.maxStreak, prev.streak + 1)
            }));

            setTimeout(() => {
                setFeedbackMessage(null);
                setCorrectAnswer(false);
                if (gameState.currentLevel < levels.length - 1) {
                    nextLevel();
                } else {
                    setGameStatus('won');
                    setShowGameWon(true);
                }
            }, 2000);
        } else {
            setFeedbackMessage(selLanguage ? "Λάθος απάντηση!" : "Wrong answer!");
            const newLives = gameState.lives - 1;

            setGameState(prev => ({
                ...prev,
                streak: 0,
                lives: newLives
            }));

            setTimeout(() => {
                setFeedbackMessage(null);
                if (newLives <= 0) {
                    setShowGameOver(true);
                    setGameStatus('lost');
                }

                setShowFeedback(false);
                setSelectedAnswer(null);
            }, 2000);
        }
    };


    const nextLevel = () => {
        setGameState(prev => ({ ...prev, currentLevel: prev.currentLevel + 1 }));
        setSelectedAnswer(null);
        setShowFeedback(false);
    };

    const resetGame = () => {
        setGameState({
            currentLevel: 0,
            score: 0,
            streak: 0,
            maxStreak: 0,
            lives: 3
        });
        setSelectedAnswer(null);
        setShowFeedback(false);
        setGameStatus('playing');
        setShowGameOver(false);
        setShowGameWon(false);
    };

    const renderHeap = (heap, title) => (
        <div style={{
            backgroundColor: "rgba(255,255,255,0.1)",
            borderRadius: "15px",
            padding: "20px",
            marginBottom: "20px",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.2)",
        }}>
            <h4 style={{ marginBottom: "15px", textAlign: "center", fontSize: "1.2rem", color: darkMode ? "#fff" : "#1e293b" }}>{title}</h4>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center" }}>
                {heap.length === 0 ? (
                    <div style={{
                        padding: "15px 25px",
                        backgroundColor: "rgba(255,255,255,0.1)",
                        borderRadius: "10px",
                        border: "2px dashed rgba(255,255,255,0.3)"
                    }}>
                        {selLanguage ? "Άδειο Heap" : "Empty Heap"}
                    </div>
                ) : (
                    heap.map((value, index) => (
                        <motion.div
                            key={index}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: index * 0.1 }}
                            style={{
                                width: "40px",
                                height: "40px",
                                backgroundColor: "#1d4ed8",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                fontSize: "1.1rem",
                                color: "white",
                                border: "2px solid rgba(255,255,255,0.3)",
                                boxShadow: "0 4px 15px rgba(0,0,0,0.2)"
                            }}
                        >
                            {value}
                        </motion.div>
                    ))
                )}
            </div>
        </div>
    );

    const containerStyle = {
        minHeight: "90vh",
        background: darkMode
            ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)"
            : "var(--lightModeGameBackground)",
        padding: "30px 10px",
        borderRadius: "10px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        overflowX: "hidden",
    };

    return (
        <div className='main-content' style={containerStyle}>
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={handlePlayAgain}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={handlePlayAgain}

            />

            <Col xl="12" lg="12" md="12" sm="12" xs="12">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    {/* Header */}
                    <div style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px",
                        gap: "15px",
                        minHeight: "50px"
                    }}>
                        {/* Επίπεδο στα αριστερά */}
                        <div style={{
                            color: darkMode ? "white" : "#1e293b",
                            padding: "12px 24px",
                            borderRadius: "12px",
                            fontWeight: "700",
                            fontSize: "18px",
                            border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}>
                            {selLanguage ? `Επίπεδο: ${gameState.currentLevel + 1}/${levels.length}` : `Level: ${gameState.currentLevel + 1}/${levels.length}`}
                        </div>

                        {/* Lives και status στα δεξιά */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            justifyContent: "flex-end",
                            gap: "15px",
                            flexWrap: "wrap",
                            minWidth: "200px"
                        }}>


                            <div style={{
                                display: "flex",
                                gap: "8px",
                                alignItems: "center"
                            }}>
                                {[1, 2, 3].map((heartNum) => (
                                    <div
                                        key={heartNum}
                                        style={{
                                            fontSize: "24px",
                                            filter: gameState.lives >= heartNum
                                                ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                                : "grayscale(100%) opacity(0.3)",
                                            transform: gameState.lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                            transition: "all 0.3s ease",
                                            animation: gameState.lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                        }}
                                    >
                                        ❤️
                                    </div>
                                ))}
                            </div>
                        </div>

                        <style>{`
        @keyframes heartbeat {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
        }
        
        @media (max-width: 768px) {
            .header-container {
                flex-direction: column;
                align-items: center;
                gap: 20px !important;
            }
        }
    `}</style>
                    </div>

                    <br></br>


                    <motion.div
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            textAlign: "center",
                            marginBottom: "30px",
                            background: "linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(29, 78, 216, 0.4))",
                            padding: "20px",
                            borderRadius: "20px",
                            backdropFilter: "blur(10px)",
                            fontSize: "1.1rem",
                            color: "#ecececff"
                        }}
                    >
                        {currentLevel.question}
                        <div style={{ display: "flex", justifyContent: "center", gap: "30px", flexWrap: "wrap" }}>

                        </div>
                    </motion.div>

                    {/* Question */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}

                    >

                        {/* Heap Visualization */}
                        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "center", alignContent: "center", }}>
                            {renderHeap(currentLevel.heap1, "Heap 1")}
                            {currentLevel.showHeap2 && renderHeap(currentLevel.heap2, "Heap 2")}
                        </div>
                    </motion.div>

                    <br />

                    {/* Answer Options */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px", marginBottom: "30px" }}>

                        {currentLevel.options.map((option, index) => {
                            const func = heapFunctions.find(f => f.name === option);
                            const isSelected = selectedAnswer === option;
                            const isCorrect = option === currentLevel.correctAnswer;


                            return (
                                <motion.button
                                    key={index}
                                    whileHover={showFeedback ? {} : { scale: 1.05 }}
                                    whileTap={showFeedback ? {} : { scale: 0.95 }}
                                    onClick={() => !showFeedback && handleAnswerSelect(option)}
                                    style={buttonStyle}
                                    disabled={showFeedback}
                                >
                                    <div style={{ marginBottom: "8px" }}>{option}</div>

                                </motion.button>
                            );
                        })}
                    </div>


                    {feedbackMessage && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            style={{
                                marginTop: "20px",
                                fontSize: "1.2rem",
                                fontWeight: "bold",
                                color: correctAnswer ? "#4ade80" : "#f87171",
                                backgroundColor: "rgba(0,0,0,0.4)",
                                padding: "12px 24px",
                                borderRadius: "12px",
                                textAlign: "center"
                            }}
                        >
                            {feedbackMessage}
                        </motion.div>
                    )}



                </div>
            </Col>
        </div>
    );
};

export default HeapFunctionsGame;